// Przeciążenie – obsługa page_type: NOT_FOUND
// @ts-nocheck

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import qs from 'query-string';

import { useGetLayoutUrlResolver } from 'api';
import { getTranslationByLanguage } from 'api/helpers';

import { Products, Product, NoMatch, CmsPage, Home } from 'pages';

interface IParams {
  category_id?: string;
  product_id?: string;
  mode?: 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS';
}

// INHB-929: slug strony CMS pełniącej rolę 404 — decyduje o noindex i statusie HTTP dla bota.
const NOT_FOUND_URL_KEY = '404';

const UrlResolver = () => {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2));
  const urlKey = pathname.replace(urlPrefix, '').replace('/', '');
  const currentLanguage = i18n.language;

  // INHB-929: adres nie jest przepisywany parametrami z url_resolver — parametry i tak są
  // odczytywane w renderContent() i przekazywane propsami, a w pasku tworzyły duplikaty dla Google.
  const {
    data: urlResolverData,
    refetch: getUrlResolver,
    status,
    error
  } = useGetLayoutUrlResolver(
    { urlKey },
    {
      enabled: false,
      keepPreviousData: true
    }
  );

  const seoData = useMemo(() => {
    if (!urlResolverData) return null;

    return {
      title: getTranslationByLanguage(urlResolverData.meta_title, currentLanguage),
      description: getTranslationByLanguage(urlResolverData.meta_description, currentLanguage),
      keywords: getTranslationByLanguage(urlResolverData.meta_keywords, currentLanguage),
      h1: getTranslationByLanguage(urlResolverData.h1, currentLanguage),
      ogTitle: getTranslationByLanguage(urlResolverData.open_graph_title, currentLanguage),
      ogDescription: getTranslationByLanguage(
        urlResolverData.open_graph_description,
        currentLanguage
      ),
      ogImage: urlResolverData.open_graph_image
    };
  }, [urlResolverData, currentLanguage]);

  useEffect(() => {
    getUrlResolver();
  }, [urlKey, currentLanguage]);

  // INHB-929: soft 404 — /pl/404 i nierozwiązane adresy oznaczamy jako nieistniejące (też dla prerendera).
  const isNotFound =
    urlKey === NOT_FOUND_URL_KEY ||
    urlResolverData?.page_type === 'NOT_FOUND' ||
    (status === 'error' && error?.httpStatus === 404);

  // Błąd inny niż 404 (np. chwilowa awaria API) — nie zgłaszamy 404, ale nie indeksujemy szkieletu.
  const isRequestError = status === 'error' && !isNotFound;

  const renderContent = () => {
    const { category_id, product_id, mode } = qs.parse(
      urlResolverData?.parameters || ''
    ) as IParams;

    const searchParams = qs.parse(search);
    const modeFromUrl = searchParams.mode as 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS' | undefined;
    const finalMode = mode || modeFromUrl;

    if (urlResolverData?.page_type === 'PRODUCTS') {
      return (
        <Products
          key="products-list"
          categoryId={category_id}
          mode={finalMode}
          h1={seoData?.h1}
          hasSeoTitle={!!seoData?.title}
        />
      );
    }

    if (urlResolverData?.page_type === 'PRODUCT' && product_id) {
      return <Product id={product_id} h1={seoData?.h1} hasSeoTitle={!!seoData?.title}/>;
    }

    if (urlResolverData?.page_type === 'CMS_PAGE') {
      return <CmsPage sectionId="SUBPAGE" articleId={urlResolverData.parameters} h1={seoData?.h1} hasSeoTitle={!!seoData?.title}/>;
    }

    if (urlResolverData?.page_type === 'HOME_PAGE') {
      return <Home />;
    }

    if (urlResolverData?.page_type === 'NOT_FOUND') {
      return <NoMatch />;
    }

    if (status === 'error') {
      // INHB-929: przy błędzie innym niż 404 pokazujemy widok 404, ale bez statusu 404 dla bota.
      return <NoMatch withNotFoundStatus={!isRequestError} />;
    }

    return null;
  };

  return (
    <>
      <Helmet>
        {seoData?.title && <title>{seoData.title}</title>}
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.ogTitle && <meta property="og:title" content={seoData.ogTitle} />}
        {seoData?.ogDescription && (
          <meta property="og:description" content={seoData.ogDescription} />
        )}
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />

        {urlResolverData?.href_lang?.map((shortcut, i) => (
          <link
            key={i}
            rel="alternate"
            href={`${window.location.origin}/${shortcut.language}/${shortcut.url_shortcut.replace(/^\//, '')}`}
            hrefLang={shortcut.language}
          />
        ))}
        {isNotFound && <meta name="robots" content="noindex, nofollow" />}
        {isNotFound && <meta name="prerender-status-code" content="404" />}
        {isRequestError && <meta name="robots" content="noindex, follow" />}
        {/* INHB-929: canonical bez parametrów zapytania (filtry/sortowanie/paginacja) */}
        <link rel="canonical" href={`${window.location.origin}${window.location.pathname}`} />
      </Helmet>
      {renderContent()}
    </>
  );
};

export default UrlResolver;
